import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@shared/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@shared/components/ui/dropdown-menu';
import { useNetworkStatus } from '@shared/hooks/useNetworkStatus';
import { useOfflineSync } from '@shared/hooks/useOfflineSync';
import { User, Wifi, WifiOff, RefreshCw } from 'lucide-react';

export default function UserProfileMenu() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { isOnline, isReconnecting } = useNetworkStatus();
  const { pendingOperations, isSyncing } = useOfflineSync();

  const toggleOfflineDebugger = () => {
    try {
      const current = localStorage.getItem('hintword_show_offline_debugger') === '1';
      localStorage.setItem('hintword_show_offline_debugger', current ? '0' : '1');
      window.dispatchEvent(new Event('offline-debugger-toggle'));
    } catch {}
  };

  // Show offline indicator if offline or has pending operations
  const showOfflineIndicator = !isOnline || pendingOperations > 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt={user.name}
              className="h-8 w-8 rounded-full object-cover"
            />
          ) : (
            <User className="h-5 w-5" />
          )}
          {/* Offline indicator badge */}
          {showOfflineIndicator && (
            <div className="absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-background">
              {!isOnline ? (
                <WifiOff className="h-2 w-2 text-red-500" />
              ) : isReconnecting ? (
                <RefreshCw className="h-2 w-2 text-yellow-500 animate-spin" />
              ) : (
                <Wifi className="h-2 w-2 text-orange-500" />
              )}
            </div>
          )}
          <span className="sr-only">Profile menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <div className="px-3 py-2 flex items-center gap-2">
          {user?.avatar && (
            <img src={user.avatar} alt={user.name} className="h-8 w-8 rounded-full object-cover" />
          )}
          <div className="min-w-0">
            <div className="font-medium text-sm truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          </div>
        </div>
        <DropdownMenuItem onClick={() => navigate('/profile')}>Profile</DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate('/org')}>Organization</DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-3 py-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            {!isOnline ? (
              <WifiOff className="h-3 w-3 text-red-500" />
            ) : isReconnecting ? (
              <RefreshCw className="h-3 w-3 text-yellow-500 animate-spin" />
            ) : (
              <Wifi className="h-3 w-3 text-green-500" />
            )}
            <span>Network: {isOnline ? 'Online' : 'Offline'} {isReconnecting ? '(Reconnecting...)' : ''}</span>
          </div>
          {pendingOperations > 0 && (
            <div className="mt-1 text-orange-500">
              {isSyncing ? 'Syncing...' : `${pendingOperations} pending sync`}
            </div>
          )}
        </div>
        <DropdownMenuItem onClick={toggleOfflineDebugger}>
          Toggle Offline Debugger
        </DropdownMenuItem>
        <DropdownMenuItem onClick={logout} className="text-destructive">Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
} 